import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { cn } from '@/utils/cn';
import { MAX_WIDTH_639PX } from '@/utils/common';
import {
  CREATOR_PAGE_COLUMN_INSET,
  getColumnRight,
} from '@/utils/common/layout';
import { Z_INDEX } from '@/utils/common/zIndex';
import { useBottomNavigation } from '@/utils/hook/react-native';
import { useHasUnreadNotificationsQuery } from '@/utils/hook/react-query/recipe/useHasUnreadNotificationsQuery';
import { useMyCreatorContentEmptyQuery } from '@/utils/hook/react-query/recipe/useMyCreatorContentEmptyQuery';
import { useRecipeDiaryNavigation } from '@/utils/hook/recipe/useRecipeDiaryNavigation';
import { useHistoryMethod } from '@/utils/hook/useHistoryMethod';
import { usePathnameMethod } from '@/utils/hook/usePathnameMethod';
import { CommunityPolicy } from '@/utils/policy/CommunityPolicy';

import RecipeCreateModal from './RecipeCreateModal';
import {
  getRecipeDiaryFloatingAction,
  RECIPE_FLOATING_ACTION_ICON,
  RecipeFloatingActionButton,
} from './RecipeFloatingActionButton';

const COMMUNITY_BUTTON_SIZE_PX = 56;
const RIGHT_MARGIN_PX = 16;
const CREATOR_PAGE_BOTTOM_MARGIN_PX = 16;

interface CommunityFloatingButtonRightParams {
  isCreatorPage: boolean;
  isMobile: boolean;
  shouldUseViewportRightMargin: boolean;
}

const getCommunityFloatingButtonRight = ({
  isCreatorPage,
  isMobile,
  shouldUseViewportRightMargin,
}: CommunityFloatingButtonRightParams) => {
  if (isCreatorPage) return getColumnRight(CREATOR_PAGE_COLUMN_INSET);
  if (isMobile) return 8;
  if (shouldUseViewportRightMargin) return RIGHT_MARGIN_PX;
  return `calc(50vw - 512px - 72px)`;
};

const useCommunityRecipeFloatingButtons = () => {
  const history = useHistoryMethod();
  const { pathname } = usePathnameMethod();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const isCreatorPage = pathname === CommunityPolicy.CREATOR_PAGE_PATH;
  const { hasUnreadNotifications } = useHasUnreadNotificationsQuery();
  const { isMyCreatorContentEmpty, isMyCreatorContentLoading } =
    useMyCreatorContentEmptyQuery({ enabled: isCreatorPage });
  const { me, handleMoveToMyDiary } = useRecipeDiaryNavigation();
  const { addBottomHeightRN } = useBottomNavigation();
  const isMobile = useMediaQuery({ query: MAX_WIDTH_639PX });
  const shouldUseViewportRightMargin = useMediaQuery({
    query: `(max-width: ${1024 + (COMMUNITY_BUTTON_SIZE_PX + RIGHT_MARGIN_PX) * 2}px)`,
  });

  const shouldShowUploadButton =
    !isCreatorPage || (!isMyCreatorContentLoading && !isMyCreatorContentEmpty);
  const diaryAction = getRecipeDiaryFloatingAction(
    me?.hasRecipeCreatorProfile === true,
  );

  return {
    isCreatorPage,
    isCreateModalOpen,
    hasUnreadNotifications,
    shouldShowUploadButton,
    shouldShowNotificationButton:
      !!me && !isCreatorPage && me.hasRecipeCreatorProfile,
    diaryTitle: diaryAction.title,
    diaryIcon: diaryAction.icon,
    bottom: isMobile || isCreatorPage ? addBottomHeightRN(8) : ('50%' as const),
    right: getCommunityFloatingButtonRight({
      isCreatorPage,
      isMobile,
      shouldUseViewportRightMargin,
    }),
    handleMoveToMyDiary,
    handleNotificationClick: () => history.push('/mypages/notifications'),
    handleOpenCreateModal: () => setIsCreateModalOpen(true),
    handleCloseCreateModal: () => setIsCreateModalOpen(false),
  };
};

interface CommunityFloatingButtonListProps {
  isCreatorPage: boolean;
  hasUnreadNotifications: boolean;
  shouldShowUploadButton: boolean;
  shouldShowNotificationButton: boolean;
  diaryTitle: string;
  diaryIcon: string;
  onDiaryClick: () => void;
  onNotificationClick: () => void;
  onUploadClick: () => void;
}

const CommunityFloatingButtonList = ({
  isCreatorPage,
  hasUnreadNotifications,
  shouldShowUploadButton,
  shouldShowNotificationButton,
  diaryTitle,
  diaryIcon,
  onDiaryClick,
  onNotificationClick,
  onUploadClick,
}: CommunityFloatingButtonListProps) => (
  <>
    {!isCreatorPage ? (
      <RecipeFloatingActionButton.Community
        title={diaryTitle}
        icon={diaryIcon}
        className="bg-gray-600"
        onClick={onDiaryClick}
      />
    ) : null}
    {shouldShowNotificationButton ? (
      <RecipeFloatingActionButton.Community
        title="알림"
        icon={RECIPE_FLOATING_ACTION_ICON.notification}
        className="bg-gray-600"
        onClick={onNotificationClick}
      >
        {hasUnreadNotifications ? <NotificationBadge /> : null}
      </RecipeFloatingActionButton.Community>
    ) : null}
    {shouldShowUploadButton ? (
      <RecipeFloatingActionButton.Community
        title="업로드"
        icon={RECIPE_FLOATING_ACTION_ICON.plus}
        className="bg-brand-1"
        onClick={onUploadClick}
      />
    ) : null}
  </>
);

const RecipeFloatingButtons = () => {
  const floatingButtons = useCommunityRecipeFloatingButtons();

  return (
    <>
      <div
        className={cn(
          'fixed flex flex-col gap-[8px]',
          !floatingButtons.isCreatorPage && 'sm:translate-y-1/2',
        )}
        style={{
          zIndex: Z_INDEX.RECIPE_FLOATING_BUTTON,
          bottom: floatingButtons.bottom,
          right: floatingButtons.right,
          marginBottom: floatingButtons.isCreatorPage
            ? CREATOR_PAGE_BOTTOM_MARGIN_PX
            : undefined,
        }}
      >
        <CommunityFloatingButtonList
          isCreatorPage={floatingButtons.isCreatorPage}
          hasUnreadNotifications={floatingButtons.hasUnreadNotifications}
          shouldShowUploadButton={floatingButtons.shouldShowUploadButton}
          shouldShowNotificationButton={
            floatingButtons.shouldShowNotificationButton
          }
          diaryTitle={floatingButtons.diaryTitle}
          diaryIcon={floatingButtons.diaryIcon}
          onDiaryClick={floatingButtons.handleMoveToMyDiary}
          onNotificationClick={floatingButtons.handleNotificationClick}
          onUploadClick={floatingButtons.handleOpenCreateModal}
        />
      </div>

      {floatingButtons.isCreateModalOpen ? (
        <RecipeCreateModal
          onClose={floatingButtons.handleCloseCreateModal}
          contentColumnInset={undefined}
        />
      ) : null}
    </>
  );
};

export default RecipeFloatingButtons;

const NotificationBadge = () => (
  <div className="animate-blink absolute right-[13px] top-[7px]">
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="12" height="12" rx="6" fill="#F14C4C" />
      <path
        d="M6.77778 3.67822H8V8.32221H6.97732L5.2517 5.81654H5.22222V8.32221H4V3.67822H5.04308L6.7415 6.17709H6.77778V3.67822Z"
        fill="white"
      />
    </svg>
  </div>
);
